import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { Fonts } from '@paperflite-marketing/cleverstory-commons';

import { FORM_STEP } from '../../../constants/widgetTypes';
import { FormContext, WidgetsContext } from '../core';
import { HubspotFormView } from './hubspot';
import { MarketoFormView } from './marketo';
import { ExternalForm } from './external';

import styles from './style.module.scss';
import { PardotFormView } from './pardot';

export const ExternalFormWidgetContext = React.createContext({
  onFormSubmit: () => { }
});

const ExternalFormWidget = function ExternalFormEditor({ className, style, widgetProps, breakpoint, children }) {
  const context = React.useContext(WidgetsContext);
  const externalFormContext = React.useContext(FormContext);

  const [externalFormState, setExternalFormState] = useState({
    formStyles: {},
    step: ''
  });

  const [externalFormContextState, setExternalFormContextState] = useState({
    parent: null
  })

  const id = _.get(widgetProps, 'uid');

  var formSteps = children.filter(item => _.get(item, 'props.widgetProps.type') === FORM_STEP)
    .map(item => _.get(item, 'props.widgetProps'));

  formSteps.push(widgetProps)
  formSteps = _.orderBy(formSteps, ['order'], ['asc']);

  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);

  var widgetStyle = {
    ...style,
    ...externalFormState.formStyles
  }

  const handleFormSubmit = (contactRequest, parent) => {

    formSteps = _.orderBy(formSteps, ['order'], ['desc']);

    const steps = formSteps.filter(item => _.get(item, 'type') === FORM_STEP)
    var successStep = steps[0];

    setExternalFormState(externalFormState => {
      return {
        ...externalFormState,
        step: _.get(widgetProps, 'step', _.get(successStep, 'uid', null))
      }
    })

    if (externalFormContext.onFormSubmit) {
      externalFormContext.onFormSubmit(context.session, id, {
        contactRequest,
        parent: parent,
        external: true,
      });
    }
  }

  useEffect(() => {
    var formStyles = getStyles(properties, breakpoint);
    setExternalFormState(externalFormState => {
      return {
        ...externalFormState,
        formStyles: formStyles
      }
    })
  }, [deviceProperties]);

  useEffect(() => {
    setExternalFormContextState(state => {
      return {
        ...state,
        onFormSubmit: handleFormSubmit,
        parent: context.parent
      }
    });
  }, [context.parent])

  return (
    <ExternalFormWidgetContext.Provider value={externalFormContextState}>
      <div id={id}
        className={cx(className, styles.form)}
        style={widgetStyle}
        data-widget-id={id}>
        {React.Children.map(children,
          child => {
            const active = _.get(child, 'props.widgetProps.uid') === externalFormState.step;

            if (!active) {
              return (
                <ExternalFormView widgetProps={widgetProps} breakpoint={breakpoint} />
              )
            }

            return (
              React.cloneElement(child, {
                activeStep: active
              }, null)
            );
          })
        }
      </div>
    </ExternalFormWidgetContext.Provider>
  )
};

export const ExternalFormView = (props) => {
  const form = _.get(props.widgetProps, "content", null);
  const uid = _.get(props.widgetProps, "uid", "");

  switch (form && form.type) {
    case "hubspot":
      return <HubspotFormView form={form} uid={uid} />;
    case "marketo":
      return <MarketoFormView form={form} uid={uid} />;
    case "pardot":
      return <PardotFormView form={form} uid={uid} widget={props.widgetProps} breakpoint={props.breakpoint} />;
    case "external":
      return <ExternalForm form={form} uid={uid} widget={props.widgetProps} breakpoint={props.breakpoint} />;
    default:
      return null;
  }
}

/**
 * Get Styles for the properties
*/
function getStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const deviceProperties = _.get(properties, `${breakpoint}`);

  const font = _.get(defaultProperties, 'font', {});

  //font-color
  const colorRgb = _.get(font, 'color', null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(font, 'fontFamily', 'inherit');
  styles = {
    ...styles,
    fontFamily: fontFamily
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'regular.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;


  //padding
  const paddingString = _.get(deviceProperties, 'padding', null)
  var padding = '20'
  if (paddingString) {
    padding = parseInt(paddingString) + 'px';
  }

  styles['padding'] = padding;


  //font-style
  const bold = _.get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //background
  const backgroundRgb = _.get(defaultProperties, 'background', null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = `rgba(${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}, ${backgroundRgb.a})`;
  }

  styles['backgroundColor'] = background;

  //border
  const border = _.get(defaultProperties, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //alignment
  const alignment = _.get(deviceProperties, 'alignment', {});

  var justifyContent = _.get(alignment, 'justifyContent', 'left');
  styles['justifyContent'] = justifyContent;

  var flexDirection = _.get(alignment, 'flexDrection', 'column');
  styles['flexDirection'] = flexDirection;

  var alignItems = _.get(alignment, 'alignItems', 'start');
  styles['alignItems'] = alignItems;

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var borderStyles = {}

  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    borderStyles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    borderStyles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    borderStyles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    borderStyles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  borderStyles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  borderStyles['borderWidth'] = borderWidget;

  return borderStyles;
}

export { ExternalFormWidget };
